import { SupDicChainData } from "@/hooks/api/useSupportedChains";
import type {
  AddressProps,
  HashProps,
  ReceivedTokenProps,
  ServiceProps,
  StatusProps,
  StatusServiceProps,
  TokenProps,
  ValueProps,
  VaultProps,
  TxValueProps,
} from "@repo/ui-widgets";
import React from "react";

import * as dn from "dnum";
import { SupText, toast } from "@repo/core-ui";
import { getExplorerUrl, getProtocolUrl, getVaultUrl } from "@/utils/urls";
import type { AddressValue, HashValue, ReceivedTokenValue, ServiceValue, StatusServiceValue, StatusValue, TextValue, TokenValue, TxValue, VaultValue } from "@/types/tx";
import { SupCopy } from "@repo/core-ui";

interface Context {
  chainMap?: Record<number, SupDicChainData>;
}

export function formatAmount(value: string, decimals: number) {
  const bigInt = BigInt(value);

  return dn.format([bigInt, decimals]);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const valueStrategy: Record<string, ((data: any, context?: Context) => any) | null> = {
  hash(data: HashValue, { chainMap = {} }: Context = {}): HashProps {
    return {
      hash: data.hash,
      status: data.status,
      chainLogo: chainMap[data.chainId]?.icon,
      chainName: chainMap[data.chainId]?.name,
      chainUrl: getExplorerUrl(chainMap[data.chainId], "tx", data.hash),
      onCopy: () => toast.success(<SupText variant="button">Copied to clipboard</SupText>, { icon: <SupCopy /> }),
    };
  },
  vault(data: VaultValue, { chainMap = {} }: Context = {}): VaultProps {
    return {
      vaultName: data.name,
      vaultUrl: getVaultUrl(data.id),
      protocolLogo: data.protocol_image && data.protocol_image !== "-" ? data.protocol_image : undefined,
      protocolName: data.protocol_name && data.protocol_image !== "-" ? data.protocol_name : undefined,
      protocolUrl: getProtocolUrl(data.protocol_id),
      chainLogo: chainMap[data.chainId]?.icon,
      chainName: chainMap[data.chainId]?.name,
      chainUrl: getExplorerUrl(chainMap[data.chainId], "address", data.address),
    };
  },
  address(data: AddressValue, { chainMap = {} }: Context = {}): AddressProps {
    return {
      address: data.address,
      chainLogo: chainMap[data.chainId]?.icon,
      chainName: chainMap[data.chainId]?.name,
      chainUrl: getExplorerUrl(chainMap[data.chainId], "address", data.address),
      onCopy: () => toast.success(<SupText variant="button">Copied to clipboard</SupText>, { icon: <SupCopy /> }),
    };
  },
  service(data: ServiceValue): ServiceProps {
    return {
      icon: data.img,
      name: data.name,
    };
  },
  "status-service"(data: StatusServiceValue): StatusServiceProps {
    return {
      img: data.img,
      name: data.name,
      url: data.url,
      status: data.status,
    };
  },
  "status-value"(data: StatusValue): StatusProps {
    return {
      status: data.status,
    };
  },
  token(data: TokenValue, { chainMap = {} }: Context = {}): TokenProps {
    return {
      amount: formatAmount(data.amount, data.decimals),
      symbol: data.symbol,
      img: data.img,
      url: getExplorerUrl(chainMap[data.chainId], "token", data.address),
    };
  },
  "received-token"(data: ReceivedTokenValue, { chainMap = {} }: Context = {}): ReceivedTokenProps {
    return {
      amount: formatAmount(data.amount, data.decimals),
      symbol: data.symbol,
      img: data.img,
      url: getExplorerUrl(chainMap[data.chainId], "token", data.address),
      chainLogo: chainMap[data.chainId]?.icon,
      chainName: chainMap[data.chainId]?.name,
      chainUrl: getExplorerUrl(chainMap[data.chainId], "address", data.address),
    };
  },
  value(data: TextValue): ValueProps {
    return {
      value: data.value,
      type: data.type ?? "string",
    };
  },
  superform: null,
} as const;

export function formatValue(data: TxValue, context: Context = {}): TxValueProps | null {
  const formatter = valueStrategy[data.component];

  if (!formatter) {
    return null;
  }

  return {
    component: data.component,
    label: data.label,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    props: formatter(data, context),
  } as TxValueProps;
}
